// ============================================================================
// OWNER RESPONSE ENABLER sonrası: cevap içeriği ARTIK geri okunabilir.
// OBT F2-B — Tek park cevabı yaşam döngüsü hook'u (owner-detail hydration)
// ============================================================================
// Her park için AYRI state (publication_id + park + response_id). Park
// değiştiğinde çağıran `key` verir → yeniden kurulur (izolasyon).
//
// Owner detail (`GET /responses/{id}/my-detail`) cevap içeriğinin (answers +
// answer_id + option + photo ilişkisi + revision + version) TEK güvenilir
// hydration kaynağıdır. Sahte kalıcılık/tahmini answer_id YOKTUR: answer_id map
// yalnız backend detail'inden kurulur.
//
// İyimser kilit: her PATCH `expected_version` gönderir; 409'da güncel taslak
// yeniden yüklenir (eski state körlemesine tekrar gönderilmez).
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createResponseDraft,
  updateResponseDraft,
  submitResponse,
  resubmitResponse,
  getMyResponseDetail,
} from './responseApi';
import { uploadResponsePhoto } from './mediaApi';
import { normalizeResponseError, isConflict } from './responseErrors';
import { validatePhotoFile } from './responseValidation';
import { RESPONSE_STATUS } from './responseEnums';

/** Tek soru için backend AnswerInput üretir (doluysa; photo tipi answer'ı ANCHOR
 *  amaçlı boş da olabilir — foto bağlanacak answer_id'nin oluşması için). */
function buildSingleAnswer(question, answerState) {
  const type = question.question_type;
  const a = answerState || {};
  if (type === 'boolean') {
    if (a.answer_boolean === true || a.answer_boolean === false) {
      return { question_id: question.question_id, answer_boolean: a.answer_boolean };
    }
    return null;
  }
  if (type === 'single_select' || type === 'multi_select') {
    if (Array.isArray(a.option_values) && a.option_values.length > 0) {
      return { question_id: question.question_id, option_values: a.option_values };
    }
    return null;
  }
  if (type === 'text') {
    if (typeof a.answer_text === 'string' && a.answer_text.trim()) {
      return { question_id: question.question_id, answer_text: a.answer_text.trim() };
    }
    return null;
  }
  if (type === 'photo') {
    // Foto tipi: metin opsiyonel; answer_id'nin OLUŞMASI için her zaman gönder.
    const body = { question_id: question.question_id };
    if (typeof a.answer_text === 'string' && a.answer_text.trim()) {
      body.answer_text = a.answer_text.trim();
    }
    return body;
  }
  // document: gönderilmez (cevaplanamaz).
  return null;
}

function buildAnswersPayload(questions, answersByQuestion) {
  return (questions || [])
    .map((q) => buildSingleAnswer(q, answersByQuestion[q.question_id]))
    .filter(Boolean);
}

/** Owner detail answers[] → form answers state (question_id → değer). */
function hydrateAnswers(detailAnswers) {
  const map = {};
  (detailAnswers || []).forEach((a) => {
    const entry = {};
    if (a.answer_boolean === true || a.answer_boolean === false) {
      entry.answer_boolean = a.answer_boolean;
    }
    if (typeof a.answer_text === 'string') entry.answer_text = a.answer_text;
    if (Array.isArray(a.option_values)) entry.option_values = a.option_values.slice();
    map[a.question_id] = entry;
  });
  return map;
}

export function useParkResponse({ publicationId, park, questions }) {
  const initialResponseId = park?.response_id || null;
  const [responseId, setResponseId] = useState(initialResponseId);
  const [status, setStatus] = useState(park?.response_status || null);
  const [version, setVersion] = useState(null);
  const [answers, setAnswers] = useState({});
  const [responseText, setResponseText] = useState('');
  const [media, setMedia] = useState([]); // owner detail media[]
  const [answerIdByQuestion, setAnswerIdByQuestion] = useState({});
  const [photoIdsByQuestion, setPhotoIdsByQuestion] = useState({});
  const [revision, setRevision] = useState(null);
  const [detailLoading, setDetailLoading] = useState(Boolean(initialResponseId));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [conflict, setConflict] = useState(false);
  const creatingRef = useRef(false);
  const abortRef = useRef(null);
  // Senkron okuma için ref'ler: setState asenkron olduğundan aynı çağrı zinciri
  // içinde (loadDetail → persist/upload) güncel değeri kapanıştan OKUYAMAYIZ.
  const versionRef = useRef(null);
  const answerIdRef = useRef({});
  // Aynı sync-okuma gerekçesi (bkz. yukarıdaki not): uploadPhoto fallback'i, foto yükleme
  // anındaki GÜNCEL cevabı kapanıştan (stale closure) DEĞİL bu ref'ten okumalı — aksi
  // halde cevap seçildikten hemen sonra yüklenen foto, cevap sunucuda var olsa bile
  // answer_id=null (ORPHAN) bağlanır ve photo_required gönderimi kilitlenir.
  const answersRef = useRef({});

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const setAnswer = useCallback((questionId, patch) => {
    setAnswers((prev) => {
      const next = { ...prev, [questionId]: { ...prev[questionId], ...patch } };
      answersRef.current = next; // sync ref for racy upload callbacks (bkz. answersRef notu)
      return next;
    });
  }, []);

  /** Owner detail'i getir ve TÜM türev state'i güncelle. */
  const loadDetail = useCallback(async (id) => {
    if (!id) return null;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const detail = await getMyResponseDetail(id, { signal: controller.signal });
    if (controller.signal.aborted) return null;
    setStatus(detail.status);
    const v = typeof detail.version === 'number' ? detail.version : null;
    versionRef.current = v;
    setVersion(v);
    setResponseText(detail.response_text || '');
    const hydrated = hydrateAnswers(detail.answers);
    setAnswers(hydrated);
    answersRef.current = hydrated; // keep the sync ref aligned with server truth
    setMedia(Array.isArray(detail.media) ? detail.media : []);
    setRevision(detail.revision || null);
    const aMap = {};
    const pMap = {};
    (detail.answers || []).forEach((a) => {
      aMap[a.question_id] = a.answer_id;
      pMap[a.question_id] = Array.isArray(a.photo_ids) ? a.photo_ids : [];
    });
    answerIdRef.current = aMap;
    setAnswerIdByQuestion(aMap);
    setPhotoIdsByQuestion(pMap);
    return detail;
  }, []);

  // Mevcut response varsa açılışta içeriğini yükle (park başına izole).
  useEffect(() => {
    let active = true;
    if (!initialResponseId) {
      setDetailLoading(false);
      return undefined;
    }
    setDetailLoading(true);
    loadDetail(initialResponseId)
      .catch((e) => {
        if (!active) return;
        const n = normalizeResponseError(e);
        if (n.status !== null) setError(n);
      })
      .finally(() => {
        if (active) setDetailLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialResponseId]);

  const run = useCallback(async (fn) => {
    setSubmitting(true);
    setError(null);
    try {
      return await fn();
    } catch (e) {
      const n = normalizeResponseError(e);
      setError(n);
      if (isConflict(n)) setConflict(true);
      return { ok: false, error: n, conflict: isConflict(n) };
    } finally {
      setSubmitting(false);
    }
  }, []);

  /** Response yoksa taslak oluştur; sonra detail'i yükle (version + answer_id). */
  const ensureDraft = useCallback(async () => {
    if (responseId) return responseId;
    if (creatingRef.current) return null;
    creatingRef.current = true;
    try {
      const payload = park?.supplemental_park_id
        ? { supplementalParkId: park.supplemental_park_id }
        : { publicationParkId: park.publication_park_id };
      const out = await createResponseDraft(publicationId, payload);
      setResponseId(out.response_id);
      setStatus(out.status || RESPONSE_STATUS.DRAFT);
      // Owner detail TEK güvenilir hydration kaynağı (version dahil). ANCAK yeni
      // oluşturulan taslak sunucuda BOŞ olduğundan loadDetail, kullanıcının henüz
      // kaydedilmemiş cevabını (ör. seçili "Evet") SIFIRLAR. Bu, cevap seçildikten hemen
      // sonra kaydet/foto-ekle akışında cevabın kaybolmasına ve fotonun answer_id=null
      // (ORPHAN) bağlanıp photo_required gönderiminin kilitlenmesine yol açar. Bu yüzden
      // client cevaplarını loadDetail ÖNCESİ yakalayıp, boş taslakta olmayanları geri koyarız.
      const clientAnswers = answersRef.current;
      await loadDetail(out.response_id);
      const hasClientAnswers = clientAnswers && Object.keys(clientAnswers).length > 0;
      const serverEmpty = !answersRef.current || Object.keys(answersRef.current).length === 0;
      if (hasClientAnswers && serverEmpty) {
        setAnswers(clientAnswers);
        answersRef.current = clientAnswers;
      }
      return out.response_id;
    } finally {
      creatingRef.current = false;
    }
  }, [responseId, publicationId, park, loadDetail]);

  /** Mevcut answers'ı expected_version ile kaydet (opsiyonel ekstra answer). */
  const persist = useCallback(
    async (id, extraAnswers = null) => {
      const payloadAnswers = extraAnswers || buildAnswersPayload(questions, answers);
      const out = await updateResponseDraft(id, {
        responseText: responseText || undefined,
        answers: payloadAnswers,
        expectedVersion:
          typeof versionRef.current === 'number' ? versionRef.current : undefined,
      });
      if (typeof out?.version === 'number') {
        versionRef.current = out.version;
        setVersion(out.version);
      }
      if (out?.status) setStatus(out.status);
      return out;
    },
    [questions, answers, responseText]
  );

  const saveDraft = useCallback(
    () =>
      run(async () => {
        const id = await ensureDraft();
        await persist(id);
        await loadDetail(id); // answer_id + medya ilişkilerini tazele
        setConflict(false);
        return { ok: true, status };
      }),
    [run, ensureDraft, persist, loadDetail, status]
  );

  /**
   * Fotoğrafı DOĞRU soruya (answer_id) bağla. Answer yoksa önce oluştur (§14).
   */
  const uploadPhoto = useCallback(
    (file, questionId) =>
      run(async () => {
        const check = validatePhotoFile(file);
        if (!check.ok) {
          const n = { status: check.code, kind: 'media', message: check.message };
          setError(n);
          return { ok: false, error: n };
        }
        const q = (questions || []).find((x) => x.question_id === questionId);
        // KÖK NEDEN FIX: ensureDraft() yeni taslak oluşturduğunda loadDetail ile client
        // cevaplarını BOŞ sunucu durumuna sıfırlar. Kullanıcının henüz kaydedilmemiş
        // cevabını (ör. "Evet") bu sıfırlamadan ÖNCE yakalamazsak, cevap seçildikten
        // hemen sonra eklenen foto answer_id=null (ORPHAN) bağlanır ve photo_required
        // gönderimi kalıcı olarak kilitlenir. Bu yüzden cevap değerini ensureDraft ÖNCESİ okuruz.
        const pendingAnswer = answersRef.current[questionId];
        const id = await ensureDraft();
        // Bu soru için answer_id yoksa önce taslağı kaydet (answer'ı oluştur).
        let answerId = answerIdRef.current[questionId];
        if (!answerId && q) {
          // Yakalanan (sıfırlamadan önceki) cevap değerinden answer'ı oluştur+kaydet ve
          // fotoyu gerçek server answer_id'sine bağla (orphan yerine).
          const single = buildSingleAnswer(q, pendingAnswer);
          if (single) {
            await persist(id, [single]);
            await loadDetail(id);
            answerId = answerIdRef.current[questionId] || null;
          }
        }
        // Yalnız gerçek backend answer_id'si ile bağla (photo/photo_required).
        const out = await uploadResponsePhoto(id, file, answerId || null);
        await loadDetail(id); // fotoğrafı doğru soru altında göstermek için
        return { ok: true, photo: out };
      }),
    // `answers` no longer read directly here (we use answersRef for the pending value),
    // so it is intentionally not a dependency.
    [run, ensureDraft, persist, loadDetail, questions]
  );

  const submit = useCallback(
    () =>
      run(async () => {
        const id = await ensureDraft();
        await persist(id);
        await loadDetail(id); // submit öncesi güncel completeness
        const out = await submitResponse(id);
        if (out?.status) setStatus(out.status);
        await loadDetail(id);
        return { ok: true, status: out?.status };
      }),
    [run, ensureDraft, persist, loadDetail]
  );

  const resubmit = useCallback(
    (comment) =>
      run(async () => {
        if (!responseId) throw new Error('Yeniden gönderilecek cevap yok.');
        await persist(responseId);
        await loadDetail(responseId);
        const out = await resubmitResponse(responseId, comment);
        if (out?.status) setStatus(out.status);
        await loadDetail(responseId);
        return { ok: true, status: out?.status, review_state: out?.review_state };
      }),
    [run, persist, loadDetail, responseId]
  );

  /** 409 sonrası: güncel taslağı yeniden yükle (eski state kaybolur uyarısı UI'de). */
  const reloadDetail = useCallback(async () => {
    if (!responseId) return;
    setConflict(false);
    setError(null);
    setDetailLoading(true);
    try {
      await loadDetail(responseId);
    } catch (e) {
      setError(normalizeResponseError(e));
    } finally {
      setDetailLoading(false);
    }
  }, [responseId, loadDetail]);

  return {
    responseId,
    status,
    version,
    answers,
    setAnswer,
    responseText,
    setResponseText,
    media,
    answerIdByQuestion,
    photoIdsByQuestion,
    revision,
    detailLoading,
    submitting,
    error,
    conflict,
    setConflict,
    ensureDraft,
    saveDraft,
    uploadPhoto,
    submit,
    resubmit,
    reloadDetail,
  };
}
